// covered-by: frontends/carrie/src/routes/tickets/routes/components/comment-drawer/__tests__/Comment.test.tsx
/**
 * The comment drawer's "Source" affordance and the dialogs behind it.
 *
 * WHAT MOVED (this module is new): the whole `renderCitation` block came out of
 * `Comment.tsx` — the source trigger, the per-variant dialog branching
 * (`KnowledgeRuleCitationDialog` vs. the generic guideline/file dialog), and
 * the Knowledge Database record fetch/state those branches read. `Comment.tsx`
 * now derives the citation list for the message and renders this component;
 * nothing else about the behavior changed.
 *
 * Kept in the `comment-drawer` folder rather than `src/components` on purpose:
 * the copy, the testids and the guest gating here are the drawer's, and the Ask
 * Puntt surface already has its own chips (`routes/ask-puntt/components/
 * CommentCitationLinks.tsx`). What the two surfaces genuinely share is the
 * dispatcher — `resolveCitationVariant` / `isDrawerRenderableVariant` in
 * `utils/citations` — which both call rather than re-deriving.
 */
import { type TicketMessage } from "@mg/schemas/src/commons";
import { ArrowSquareOut, Warning } from "@phosphor-icons/react";
import { Box, Button, Dialog, Flex, Text, Tooltip } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { KnowledgeRuleCitationDialog } from "../../../../../components/KnowledgeRuleCitationDialog";
import { Preview } from "../../../../../components/Preview";
import { useIsFlagOn } from "../../../../../config/featureFlags";
import { isPunttGuest } from "../../../../../utils/auth";
import {
  isDrawerRenderableVariant,
  isKnowledgeRuleCitation,
  resolveCitationVariant,
} from "../../../../../utils/citations";
import { type KnowledgeDataRecordProperty } from "../../../../../utils/parsers/knowledge-db";
import { useGetKnowledgeDataRecord } from "../../../../../utils/queries/knowledge-db";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  knowledgeDatabaseRoute,
} from "../../../../settings/routes/knowledge-database/route";
import { knowledgeSettingRoute } from "../../../../settings/routes/knowledgeSettingRoute";

type CommentCitationSourceProps = {
  /**
   * The citations attached to the message being rendered — already resolved by
   * `Comment` (a reply reads its own `citations` prop; a root comment falls
   * back to `meta.citations`). The first entry is the primary citation that
   * picks the dialog; later text citations are stacked into the generic one.
   */
  citations?: TicketMessage["citations"];
};

export function CommentCitationSource({
  citations,
}: CommentCitationSourceProps) {
  const { t } = useTranslation("ui");
  const isGuest = isPunttGuest();
  const canManageBrandGuidelines = useIsFlagOn("manage-brand-guidelines");

  const moreTextCitations = citations
    ?.slice(1)
    .filter(
      (citation): citation is { text: string } =>
        "text" in citation && Boolean(citation.text),
    );
  // Being a little less strict with the union of citation types makes this much easier to work with
  const citation =
    (citations?.[0] as {
      url?: string;
      pageNumber?: number;
      text?: string;
      knowledgeDataRecordId?: string;
    }) || false;

  const getKnowledgeDataRecord = useGetKnowledgeDataRecord(
    citation ? citation.knowledgeDataRecordId : undefined,
  );
  const [knowledgeDataRecord, setKnowledgeDataRecord] =
    useState<KnowledgeDataRecordProperty[]>();
  useEffect(() => {
    if (!citation || !citation.knowledgeDataRecordId) return;
    const data = getKnowledgeDataRecord.data;
    if (!data) return;
    setKnowledgeDataRecord(data.data);
  }, [citation, getKnowledgeDataRecord.data]);

  if (!citation) return null;

  const primaryCitation = citations?.[0];
  // Same dispatcher the Ask Puntt comment panel (`CommentCitationLinks`)
  // runs on: `resolveCitationVariant` in utils/citations. This drawer used
  // to test `isKnowledgeRuleCitation` alone and let every other arm of the
  // union fall through to the generic dialog below, whose `Preview` then got
  // `url` / `text` / `knowledgeDataRecord` all undefined -- a "Source" link
  // that opens "Sorry, we can't preview this file type." The two surfaces
  // disagreed about which citations were renderable; now the shared resolver
  // plus `isDrawerRenderableVariant` says so in one place.
  const variant =
    primaryCitation != null ? resolveCitationVariant(primaryCitation) : null;
  // Belt: a graph or agent-source citation carries nothing the generic
  // dialog can show, so it gets no source affordance at all instead of a
  // link to an empty dialog. Braces: the generic dialog is still offered for
  // an odd shape it CAN render (e.g. an excerpt alongside an empty `url`,
  // which the shared resolver cannot call a text citation), so narrowing the
  // list above never silently hides a citation a reader could have read.
  const hasGenericDialogContent = Boolean(
    citation.url || citation.text || citation.knowledgeDataRecordId,
  );
  if (
    variant != null &&
    !isDrawerRenderableVariant(variant) &&
    !hasGenericDialogContent
  ) {
    return null;
  }

  if (citation.knowledgeDataRecordId && !knowledgeDataRecord) {
    if (getKnowledgeDataRecord.isError) {
      let errorMessage = "Error loading source details";
      const error = getKnowledgeDataRecord.error;
      if (error instanceof Response && error.status === 404) {
        errorMessage = "Source was deleted from the Knowledge Database";
      } else if (error.message !== "Failed to fetch") {
        errorMessage += `: ${error.message}`;
      }

      return (
        <Tooltip content={errorMessage}>
          <Flex
            className="mt-2 w-max cursor-not-allowed text-puntt-accent-12 opacity-50"
            gap="1"
          >
            <Text className="underline" size="3">
              {t("tickets.comment.source")}
            </Text>
            <Warning width={16} height={16} className="mt-1" />
          </Flex>
        </Tooltip>
      );
    }

    return (
      <Flex
        className="mt-2 w-max cursor-wait text-puntt-accent-12 opacity-50"
        gap="1"
      >
        <Text className="underline" size="3">
          {t("tickets.comment.source")}
        </Text>
        <ArrowSquareOut width={16} height={16} className="mt-1" />
      </Flex>
    );
  }

  const sourceTrigger = (
    <Dialog.Trigger>
      <Flex
        data-testid="comment-citation-source-trigger"
        aria-label={t("tickets.comment.openCommentSourceDetails")}
        className="mt-2 w-max cursor-pointer text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
        gap="1"
      >
        <Text className="underline" size="3">
          {t("tickets.comment.source")}
        </Text>
        <ArrowSquareOut width={16} height={16} className="mt-1" />
      </Flex>
    </Dialog.Trigger>
  );

  // `isKnowledgeRuleCitation` rather than `variant === "rule"` only because
  // the predicate NARROWS `primaryCitation` to the rule arm for the dialog's
  // prop; it is the same test the shared resolver runs for that variant.
  if (
    variant === "rule" &&
    primaryCitation != null &&
    isKnowledgeRuleCitation(primaryCitation)
  ) {
    return (
      <KnowledgeRuleCitationDialog
        citation={primaryCitation}
        showKnowledgeDatabaseLink={!isGuest}
        allowSourcePreview={!isGuest}
      >
        {sourceTrigger}
      </KnowledgeRuleCitationDialog>
    );
  }

  return (
    <Dialog.Root>
      {sourceTrigger}
      <Dialog.Content
        data-testid="comment-citation-source-dialog"
        className="flex flex-col gap-4"
        style={
          !citation.url && (citation.text || citation.knowledgeDataRecordId)
            ? {} // Smaller width for text-only citations
            : {
                width: "90vw",
                maxWidth: "80rem",
                height: "90vh",
                maxHeight: "60rem",
              }
        }
      >
        <Flex justify="between" align="center" gap="4">
          <Dialog.Title className="mb-0">
            {t("tickets.comment.commentSourceDetails")}
          </Dialog.Title>
          <Dialog.Description className="hidden">
            {t("tickets.comment.citationDetails")}
          </Dialog.Description>
          <Flex gap="2" align="center">
            {/* Use <a> for external URLs to preserve hash fragments, <Link> for internal routes */}
            {citation.url && !knowledgeDataRecord ? (
              <Button
                className={cx(
                  "hover:bg-puntt-accent-10 hover:text-base-white",
                  { hidden: isGuest },
                )}
                variant="outline"
                size="2"
                asChild
              >
                <a
                  data-testid="comment-a-1297"
                  href={
                    citation.url.endsWith(".pdf")
                      ? citation.url +
                        "?response-content-disposition=inline&response-content-type=application%2Fpdf" +
                        `#page=${citation.pageNumber ?? 1}`
                      : citation.url
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="puntt-link flex gap-1"
                >
                  <Text>{t("tickets.comment.openNewTab")}</Text>
                  <ArrowSquareOut width={16} height={16} />
                </a>
              </Button>
            ) : (
              <Button
                className={cx(
                  "hover:bg-puntt-accent-10 hover:text-base-white",
                  {
                    hidden:
                      (!canManageBrandGuidelines && !knowledgeDataRecord) ||
                      isGuest,
                  },
                )}
                variant="outline"
                size="2"
                asChild
              >
                {/* Split links: a dynamic `to` cannot be typed against two
                    routes' search schemas (the KD route requires its full
                    default search; knowledge settings does not take it). */}
                {knowledgeDataRecord ? (
                  <Link
                    data-testid="comment-citation-knowledge-database-link"
                    to={knowledgeDatabaseRoute.to}
                    search={KNOWLEDGE_DATABASE_DEFAULT_SEARCH}
                    target="_blank"
                    rel="noreferrer"
                    className="puntt-link flex gap-1"
                  >
                    <Text>{t("tickets.comment.viewKnowledgeDatabase")}</Text>
                    <ArrowSquareOut width={16} height={16} />
                  </Link>
                ) : (
                  <Link
                    data-testid="comment-citation-knowledge-settings-link"
                    to={knowledgeSettingRoute.to}
                    target="_blank"
                    rel="noreferrer"
                    className="puntt-link flex gap-1"
                  >
                    <Text>{t("tickets.comment.viewKnowledgeSettings")}</Text>
                    <ArrowSquareOut width={16} height={16} />
                  </Link>
                )}
              </Button>
            )}
            {(citation as any).originalURL && (
              <Button
                className="hover:bg-puntt-accent-10 hover:text-base-white"
                variant="outline"
                size="2"
                asChild
              >
                <Link
                  to={(citation as any).originalURL}
                  target="_blank"
                  rel="noreferrer"
                  className="puntt-link flex gap-1"
                >
                  <Text>{t("tickets.comment.openOriginalUrl")}</Text>
                  <ArrowSquareOut width={16} height={16} />
                </Link>
              </Button>
            )}
          </Flex>
          <Dialog.Close>
            <Button type="button" variant="soft" color="gray">
              {t("common:actions.close")}
            </Button>
          </Dialog.Close>
        </Flex>
        {citation.text ? (
          // Multiple claims can be cited by a single comment; render each
          // as its own bordered card so they don't visually bleed into
          // each other as one wall of markdown.
          <Flex direction="column" gap="3">
            <Text size="2" className="text-puntt-neutral-gray-11">
              <em>{t("tickets.comment.fromCustomGuidelines")}</em>
            </Text>
            {[
              citation.text,
              ...(moreTextCitations ?? []).map((c) => c.text),
            ].map((text, i) => (
              <Box
                key={i}
                className="rounded-md border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3"
              >
                <Preview text={text} />
              </Box>
            ))}
          </Flex>
        ) : (
          <Preview
            url={citation.url}
            pageNumber={citation.pageNumber}
            text={citation.text}
            textPrefix="From custom guidelines:"
            knowledgeDataRecord={knowledgeDataRecord}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
